import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styles from "../PublicCollab/PublicCollab.module.sass";
import { getAuthToken, isTokenExpired } from "../../utils/authHelpers";

const TEASER_COPY = "Join Postaway to connect and collaborate with this property plus many more hospitality partners.";

const AMENITY_LABELS = {
    hot_tub: "Hot Tub", swimming_pool: "Pool", sauna: "Sauna", cold_plunge: "Cold Plunge",
    firepit: "Firepit", outdoor_deck: "Outdoor Deck", lake: "Lake", mountains: "Mountains",
    beach: "Beach", golf: "Golf", skiing: "Skiing", pet_friendly: "Pet Friendly",
    family_friendly: "Family Friendly", free_parking: "Free Parking", ev_charger: "EV Charger",
    grill: "Grill", smartlock: "Smart Lock", low_toxin: "Low Toxin", accessible: "Accessible",
    river_access: "River Access", pickleball: "Pickleball", tennis_courts: "Tennis Courts",
};

const setReturnToCurrentPage = () => {
    try {
        const path = `${window.location.pathname}${window.location.search || ""}`;
        sessionStorage.setItem("postaway_return_to", path);
    } catch (_) {}
};

const PublicProperty = () => {
    const { idOrSlug } = useParams();
    const navigate = useNavigate();
    const [listing, setListing] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [checkingAccess, setCheckingAccess] = useState(true);

    useEffect(() => {
        let cancelled = false;

        const load = async () => {
            setLoading(true);
            setError("");
            try {
                const res = await fetch(`/api/public/collab?type=listing&id=${encodeURIComponent(idOrSlug || "")}`);
                const data = await res.json();
                if (cancelled) return;

                if (res.ok && data.ok) {
                    setListing(data);
                } else {
                    setError("This property could not be found.");
                }
            } catch (_) {
                if (!cancelled) setError("We could not load this property.");
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        load();
        return () => { cancelled = true; };
    }, [idOrSlug]);

    // Active members go straight to the property in the hosts directory.
    useEffect(() => {
        let cancelled = false;

        const redirectMembers = async () => {
            if (!listing?.id) {
                setCheckingAccess(false);
                return;
            }

            const token = getAuthToken();
            const email = (() => {
                try { return localStorage.getItem("postaway_email") || ""; } catch (_) { return ""; }
            })();

            if (!token || isTokenExpired(token) || !email) {
                setCheckingAccess(false);
                return;
            }

            try {
                const res = await fetch("/api/access/subscription", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ email }),
                });
                const data = await res.json();
                if (cancelled) return;

                const roles = data.roles || [];
                const isMember = res.ok && data.ok && data.active && roles.some((role) => role === "creator" || role === "host");
                if (isMember) {
                    navigate(`/hosts?property_id=${encodeURIComponent(listing.id)}`, { replace: true });
                    return;
                }
            } catch (_) {
                // If access check fails, leave the public page available.
            }

            if (!cancelled) setCheckingAccess(false);
        };

        redirectMembers();
        return () => { cancelled = true; };
    }, [listing, navigate]);

    const handleSignupCta = () => {
        setReturnToCurrentPage();
        navigate("/profile-selector");
    };

    if (loading || checkingAccess) {
        return (
            <main className={styles.page}>
                <div className={styles.loadingCard}>Loading property...</div>
            </main>
        );
    }

    if (error || !listing) {
        return (
            <main className={styles.page}>
                <div className={styles.emptyState}>
                    <h1>Property not found</h1>
                    <p>{error || "This property is no longer available."}</p>
                    <button type="button" onClick={() => navigate("/")} className={styles.secondaryButton}>Back to Postaway</button>
                </div>
            </main>
        );
    }

    const stats = [
        listing.bedrooms != null && `${listing.bedrooms} BR`,
        listing.bathrooms != null && `${listing.bathrooms} BA`,
        listing.sleeps != null && `Sleeps ${listing.sleeps}`,
    ].filter(Boolean);

    const amenities = (listing.amenities || [])
        .map((key) => AMENITY_LABELS[key] || key.replace(/_/g, " "))
        .slice(0, 10);

    return (
        <main className={styles.page}>
            <div className={styles.postcardBorder}>
                <div className={styles.postcardInner}>
                    <header className={styles.nav}>
                        <a href="/" className={styles.logo} aria-label="Go to Postaway home">
                            <div className={styles.textLogo}>postaway<sup className={styles.tm}>TM</sup></div>
                            <div className={styles.textTagline}>SEARCH. STAY. SHARE.</div>
                        </a>
                        <button type="button" onClick={handleSignupCta} className={styles.navCta}>Join Postaway</button>
                    </header>

                    <section className={styles.hero}>
                        <div className={styles.heroImageWrap}>
                            {listing.heroImageUrl ? (
                                <img src={listing.heroImageUrl} alt={listing.title} className={styles.heroImage} />
                            ) : (
                                <div className={styles.heroFallback}>postaway</div>
                            )}
                        </div>

                        <div className={styles.heroContent}>
                            <div className={styles.eyebrow}>Featured Property</div>
                            <h1 className={styles.location}>{listing.title}</h1>
                            {listing.location && (
                                <p style={{ margin: "4px 0 0", fontWeight: 600, color: "#4b5563" }}>{listing.location}</p>
                            )}
                            {stats.length > 0 && (
                                <p style={{ margin: "10px 0 0", color: "#374151", fontSize: 15 }}>{stats.join(" · ")}</p>
                            )}
                            {amenities.length > 0 && (
                                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, margin: "14px 0 0" }}>
                                    {amenities.map((label) => (
                                        <span
                                            key={label}
                                            style={{
                                                padding: "4px 10px",
                                                borderRadius: 999,
                                                background: "#f3f4f6",
                                                color: "#374151",
                                                fontSize: 13,
                                                fontWeight: 600,
                                            }}
                                        >
                                            {label}
                                        </span>
                                    ))}
                                </div>
                            )}
                            {listing.description && (
                                <p className={styles.teaser} style={{ marginTop: 16 }}>{listing.description}</p>
                            )}
                            <p className={styles.teaser}>{TEASER_COPY}</p>
                            <div className={styles.ctaRow}>
                                <button type="button" onClick={handleSignupCta} className={styles.primaryButton}>
                                    Join Postaway
                                </button>
                                <button type="button" onClick={handleSignupCta} className={styles.secondaryButton}>
                                    Request to Collaborate
                                </button>
                            </div>
                        </div>
                    </section>

                    <section className={styles.footerCta}>
                        <h2>Want to stay or collab here?</h2>
                        <p>Create a Postaway profile to connect with {listing.ownerDisplayName || "this host"} and discover more creator-host collaborations.</p>
                        <button type="button" onClick={handleSignupCta} className={styles.primaryButton}>
                            Get Started
                        </button>
                    </section>
                </div>
            </div>
        </main>
    );
};

export default PublicProperty;
